/**
 * GuildThemePicker — footer dropdown for choosing a guild color theme.
 * Menu is portaled to <body> so it isn't clipped by the footer's overflow-hidden container.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronUp, Palette } from 'lucide-react';
import { GUILD_THEMES, useGuildTheme } from '../lib/guildTheme';

export function SwatchPair({ swatches, size = 'size-3' }: { swatches: [string, string]; size?: string }) {
    return (
        <span className="flex -space-x-1 shrink-0">
            {swatches.map((c, i) => (
                <span
                    key={i}
                    className={`${size} rounded-full ring-1 ring-black/60`}
                    style={{ backgroundColor: c, boxShadow: `0 0 6px ${c}80` }}
                />
            ))}
        </span>
    );
}

export function GuildThemePicker() {
    const [theme, setTheme] = useGuildTheme();
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState<{ left: number; bottom: number } | null>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const active = GUILD_THEMES.find(t => t.id === theme) ?? GUILD_THEMES[0];

    // Position the menu above the trigger
    useLayoutEffect(() => {
        if (!open || !triggerRef.current) return;
        const update = () => {
            const r = triggerRef.current!.getBoundingClientRect();
            const menuWidth = 208;
            const left = Math.min(Math.max(8, r.left + r.width / 2 - menuWidth / 2), window.innerWidth - menuWidth - 8);
            setPos({ left, bottom: window.innerHeight - r.top + 8 });
        };
        update();
        window.addEventListener('resize', update);
        window.addEventListener('scroll', update, true);
        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, true);
        };
    }, [open]);

    // Close on outside click / Escape
    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            const t = e.target as Node;
            if (!menuRef.current?.contains(t) && !triggerRef.current?.contains(t)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') { setOpen(false); triggerRef.current?.focus(); }
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                onClick={() => setOpen(o => !o)}
                className="flex items-center gap-2 px-3 py-1.5 bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/20 hover:border-purple-500/40 rounded-lg transition-all group"
                title="Choose a color theme"
                aria-haspopup="menu"
                aria-expanded={open}
            >
                <Palette className="size-4 text-amber-400" />
                <div className="flex flex-col items-start">
                    <span className="text-white/70 text-xs font-medium group-hover:text-amber-300 transition-colors flex items-center gap-1.5">
                        {active.name}
                        <SwatchPair swatches={active.swatches} size="size-2.5" />
                    </span>
                    <span className="text-white/30 text-[10px]">Theme</span>
                </div>
                <ChevronUp className={`size-3 text-white/40 transition-transform ${open ? '' : 'rotate-180'}`} />
            </button>

            {open && pos && createPortal(
                <div
                    ref={menuRef}
                    role="menu"
                    aria-label="Color theme"
                    className="fixed z-[100] w-52 max-h-[70vh] overflow-y-auto rounded-xl border border-purple-500/30 bg-[#0a0a1a]/95 backdrop-blur-md p-1.5 shadow-2xl shadow-black/60"
                    style={{ left: pos.left, bottom: pos.bottom }}
                >
                    <div className="px-2 pt-1 pb-1.5 text-[10px] uppercase tracking-wider text-white/40">Color Theme</div>
                    {GUILD_THEMES.map((t, i) => {
                        const selected = t.id === theme;
                        return (
                            <div key={t.id}>
                                {i === 1 && <div className="my-1 h-px bg-white/10" />}
                                <button
                                    type="button"
                                    role="menuitemradio"
                                    aria-checked={selected}
                                    onClick={() => { setTheme(t.id); setOpen(false); }}
                                    className={`w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-left transition-colors ${selected ? 'bg-purple-500/20' : 'hover:bg-white/5'}`}
                                >
                                    <SwatchPair swatches={t.swatches} />
                                    <span className="flex-1 min-w-0">
                                        <span className={`block text-xs font-medium ${selected ? 'text-amber-300' : 'text-white/80'}`}>{t.name}</span>
                                        <span className="block text-[10px] text-white/35">{t.colors}</span>
                                    </span>
                                    {selected && <Check className="size-3.5 text-amber-400 shrink-0" />}
                                </button>
                            </div>
                        );
                    })}
                </div>,
                document.body
            )}
        </>
    );
}

export default GuildThemePicker;
